import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, delay, exhaustMap, map, of } from 'rxjs';
import { mapResponse } from '@ngrx/operators';
import { LoadingState } from '../models/loader.model';

import { CompanyService, CompanySitesService } from '../../../api/services';
import { companiesActions } from './companies.actions';
import { getErrorMessage, ZetraError } from '../../shared/errors/error-messages.helper';

export const companiesLoadEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.load),
      exhaustMap((props) =>
        compService.companyControllerFind({ ...props }).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.done({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.done({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companiesLoadOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadOne),
      exhaustMap((props) =>
        compService.companyControllerFindOne(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.doneOne({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.doneOne({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companiesUploadLogoEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.uploadLogo),
      exhaustMap((props) =>
        compService.companyControllerAddLogo(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.uploadLogoDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.uploadLogoFail({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companiesLoadUsersEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadUsers),
      exhaustMap((props) =>
        compService.companyControllerGetAssignedUsers(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.loadUsersDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadUsersDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companySiteAssignUsersEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.assignUsers),
      exhaustMap((props) =>
        compSiteService.companySiteControllerAssociateUsersToCompanySite(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.assignUsersDone({ state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.assignUsersDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companySiteUnassignUsersEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.unassignUsers),
      exhaustMap((props) =>
        compSiteService.companySiteControllerDissociateUsersFromCompanySite(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.unassignUsersDone({ state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.unassignUsersDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyRemoveUsersEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.removeUsersFromCompany),
      exhaustMap((props) =>
        compService.companyControllerRemoveUsers(props).pipe(
          delay(1000),
          mapResponse({
            next: () => companiesActions.removeUsersFromCompanyDone({ state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.removeUsersFromCompanyDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyAddUsersEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.addUsersToCompany),
      exhaustMap((props) =>
        compService.companyControllerAddUsers(props).pipe(
          delay(1000),
          mapResponse({
            next: () => companiesActions.addUsersToCompanyDone({ state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.addUsersToCompanyDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyLoadAddressBook = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadAddressBook),
      exhaustMap((props) =>
        compService.companyControllerFindAddressBookItems(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.loadAddressBookDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadAddressBookDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companySiteAddContactToAddressBookEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.addContactoToAddressBook),
      exhaustMap((props) =>
        compSiteService.companySiteControllerCreateAddressBookOfanCompanySite(props).pipe(
          mapResponse({
            next: (r) =>
              companiesActions.addContactoToAddressBookDone({
                state: { value: LoadingState.SUCCESS },
                data: r.data,
              }),
            error: (error: ZetraError) =>
              companiesActions.addContactoToAddressBookDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const saveOneAddressBookContactEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.saveOneAddressBookContact),
      exhaustMap((props) =>
        compSiteService.companySiteControllerUpdateAddressBook(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.saveOneDoneAddressBookContact({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              companiesActions.saveOneDoneAddressBookContact({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const loadOneAddressBookContactEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadOneAddressBookContact),
      exhaustMap((props) =>
        compSiteService.companySiteControllerFindOneAddressBook(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadOneDoneAddressBookContact({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadOneDoneAddressBookContact({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const deleteOneAddressBookContactEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.deleteOneAddressBookContact),
      exhaustMap((props) =>
        compSiteService.companySiteControllerDeleteAddressBook(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.deleteOneAddressBookContactDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              companiesActions.deleteOneAddressBookContactDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const companyCreateOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.createOne),
      exhaustMap((props) =>
        compService.companyControllerCreate(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.createOneDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.createOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companySaveOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.saveOne),
      exhaustMap((props) =>
        compService.companyControllerUpdate(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.saveOneDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.saveOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyDeleteOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.moveToTrash),
      exhaustMap((props) =>
        compService.companyControllerMoveToTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.moveToTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.moveToTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companiesLoadTrash = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadTrash),
      exhaustMap((props) =>
        compService.companyControllerCompaniesInTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.loadTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyDeleteOneFromTrashOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.forceDelete),
      exhaustMap((props) =>
        compService.companyControllerForceDelete(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.forceDeleteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.forceDeleteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
export const companyRestoreOneFromTrashOneEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.restoreFromTrash),
      exhaustMap((props) =>
        compService.companyControllerRestoreFromTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.restoreFromTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.restoreFromTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const companyGetTypesEffect = createEffect(
  (actions$ = inject(Actions), compService = inject(CompanyService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadGetTypes),
      exhaustMap((props) =>
        compService.companyControllerGetTypes(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => companiesActions.doneGetTypes({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.doneGetTypes({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//lista sedi azienda
export const companyGetCompanySitesEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadCompanySites),
      exhaustMap((props) =>
        compSiteService.companySiteControllerFind(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadCompanySitesDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadCompanySitesDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//recupera una sede
export const companyGetOneCompanySitesEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadOneCompanySite),
      exhaustMap((props) =>
        compSiteService.companySiteControllerFindOne(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadOneCompanySiteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadOneCompanySiteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//crea sede
export const companyCreateCompanySiteEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.createCompanySite),
      exhaustMap((props) =>
        compSiteService.companySiteControllerRegisterCompanySiteToCompany(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.createCompanySiteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.createCompanySiteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//aggiorna sede
export const companyUpdateCompanySiteEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.updateCompanySite),
      exhaustMap((props) =>
        compSiteService.companySiteControllerUpdateCompanySiteId(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.updateCompanySiteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.updateCompanySiteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//disattiva sede
export const companyDeleteCompanySiteEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.moveCompanySiteToTrash),
      exhaustMap((props) =>
        compSiteService.companySiteControllerMoveToTrash(props).pipe(
          mapResponse({
            next: (r) =>
              companiesActions.moveCompanySiteToTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.moveCompanySiteToTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//riattiva sede
export const restoreCompanySiteFromTrashEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.restoreCompanySite),
      exhaustMap((props) =>
        compSiteService.companySiteControllerRestoreFromTrash(props).pipe(
          mapResponse({
            next: (r) =>
              companiesActions.restoreCompanySiteDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              companiesActions.restoreCompanySiteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//recupera tipi di sede
export const companyCompanySiteTypeLoadEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadCompanySiteTypes),
      exhaustMap((props) =>
        compSiteService.companySiteControllerFindAllCompanySiteTypes().pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadCompanySiteTypesDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadCompanySiteTypesDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//findAll utenti sede
export const companySiteLoadUsersEffect = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadCompanySiteUsers),
      exhaustMap((props) =>
        compSiteService.companySiteControllerGetAssignedUsers(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadCompanySiteUsersDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadCompanySiteUsersDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//findAll contatti sede
export const companySiteLoadAddressBook = createEffect(
  (actions$ = inject(Actions), compSiteService = inject(CompanySitesService)) => {
    return actions$.pipe(
      ofType(companiesActions.loadCompanySiteAddressBook),
      exhaustMap((props) =>
        compSiteService.companySiteControllerFindAllAddressBookByCompanySite(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              companiesActions.loadCompanySiteAddressBookDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              companiesActions.loadCompanySiteAddressBookDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
